import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AreaChart from '../components/charts/AreaChart';
import DoughnutChart from '../components/charts/DoughnutChart';
import { Swiper, SwiperSlide } from 'swiper/react';
import CircleProgress from '../components/charts/CircleProgress';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import Responsive from 'datatables.net-responsive';
import MobileFooter from '../components/MobileFooter';

DataTable.use(DT);
DataTable.use(Responsive);

const FinanceDashboard: React.FC = () => {
    const [dtApi, setDtApi] = useState<any>(null);

    const options = useMemo(() => ({
        responsive: true,
        pageLength: 6,
        lengthChange: false,
        searching: true,
        dom: 'rtip',
        initComplete: (settings: any) => {
            const api = new DT.Api(settings);
            setDtApi(api);
        }
    }), []);

    return (
        <>
            {/* page title */}
            <div className="container py-3">
                <div className="row align-items-center page-title">
                    <div className="col col-sm mb-3 mb-sm-0">
                        <h6 className="mb-0">Finance Dashboard</h6>
                        <p className="text-secondary small">Potential with Income and Expense Tracking</p>
                    </div>
                    <div className="col-auto ps-0 position-relative mb-3 mb-sm-0">
                        <button className="btn btn-link btn-square" type="button" data-bs-toggle="offcanvas" data-bs-target="#filter" aria-expanded="false">
                            <i className="bi bi-funnel"></i>
                        </button>
                    </div>
                </div>
                <nav aria-label="breadcrumb" className="breadcrumb-theme bg-theme-1-subtle px-2 py-1 mt-0 mt-sm-3 rounded">
                    <ol className="breadcrumb mb-0">
                        <li className="breadcrumb-item bi"><Link to="/app/dashboard"><i className="bi bi-house-door me-2"></i> DevUIUX</Link></li>
                        <li className="breadcrumb-item bi active" aria-current="page">Finance</li>
                    </ol>
                </nav>
            </div>

            {/* content */}
            <div className="container" id="main-content">

                {/* summary blocks */}
                <div className="row gx-3 gx-lg-4">
                    <div className="col-6 col-md-6 col-lg-6 col-xxl-3">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <div className="row gx-3 align-items-center">
                                    <div className="col-auto theme-blue">
                                        <div className="avatar avatar-60 rounded-circle">
                                            <CircleProgress percentage={75} color="#0049e8" size={60} />
                                            <div className="avatar avatar-40 h5 bg-theme-1-subtle text-theme-1 rounded-circle position-absolute top-50 start-50 translate-middle">
                                                <i className="bi bi-receipt"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm mt-3 mt-sm-0">
                                        <p className="text-secondary small mb-1">Total Bills</p>
                                        <h5>$ 25486.00</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-6 col-lg-6 col-xxl-3">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <div className="row gx-3 align-items-center">
                                    <div className="col-auto theme-yellow">
                                        <div className="avatar avatar-60 rounded-circle">
                                            <CircleProgress percentage={60} color="#ffc107" size={60} />
                                            <div className="avatar avatar-40 h5 bg-theme-1-subtle text-theme-1 rounded-circle position-absolute top-50 start-50 translate-middle theme-yellow">
                                                <i className="bi bi-credit-card"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm mt-3 mt-sm-0">
                                        <p className="text-secondary small mb-1">Paid Bills</p>
                                        <h5>$ 12550</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-6 col-lg-6 col-xxl-3">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <div className="row gx-3 align-items-center">
                                    <div className="col-auto theme-red">
                                        <div className="avatar avatar-60 rounded-circle">
                                            <CircleProgress percentage={45} color="#c80036" size={60} />
                                            <div className="avatar avatar-40 h5 bg-theme-1-subtle text-theme-1 rounded-circle position-absolute top-50 start-50 translate-middle theme-red">
                                                <i className="bi bi-receipt-cutoff"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm mt-3 mt-sm-0">
                                        <p className="text-secondary small mb-1">Unpaid Bills</p>
                                        <h5>$ 1525.12</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-6 col-lg-6 col-xxl-3">
                        <div className="card bg-theme-1 mb-3 mb-lg-4">
                            <div className="card-body">
                                <div className="row gx-3 align-items-center">
                                    <div className="col-auto">
                                        <div className="avatar avatar-60 rounded-circle">
                                            <CircleProgress percentage={85} color="#ffffff" size={60} />
                                            <div className="avatar avatar-40 h5 bg-white-opacity text-white rounded-circle position-absolute top-50 start-50 translate-middle">
                                                <i className="bi bi-envelope-check"></i>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-sm mt-3 mt-sm-0">
                                        <p className="opacity-50 small mb-1">Sent Bills</p>
                                        <h5>$ 15451.05</h5>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gx-3 gx-lg-4">
                    {/* balance sheet */}
                    <div className="col-12 col-sm-6 col-xxl-3 order-1 order-xxl-1 mb-3 mb-lg-4">
                        <div className="card h-100 overflow-hidden">
                            <div className="card-body">
                                <i className="bi bi-bank avatar avatar-50 rounded bg-theme-1-subtle text-theme-1 fs-4 mb-3"></i><br />
                                <h6 className="text-secondary mb-1">Welcome,</h6>
                                <h4>AdminUIUX</h4>

                                <div className="row gx-2">
                                    <div className="col-auto py-1">
                                        <a className="btn btn-square btn-theme rounded-circle" data-bs-toggle="modal" data-bs-target="#qrcodemodal" title="Scan to pay"><i className="bi bi-qr-code"></i></a>
                                    </div>
                                    <div className="col-auto py-1">
                                        <a className="btn btn-square btn-outline-theme rounded-circle" data-bs-toggle="modal" data-bs-target="#sendmoneymodal" title="Send Money"><i className="bi bi-arrow-up-right"></i></a>
                                    </div>
                                    <div className="col-auto py-1">
                                        <a className="btn btn-square btn-outline-theme rounded-circle" data-bs-toggle="modal" data-bs-target="#receivemoneymodal" title="Receive Money"><i className="bi bi-arrow-down-left"></i></a>
                                    </div>
                                    <div className="col-auto py-1">
                                        <a className="btn btn-square btn-link rounded-circle" data-bs-toggle="modal" data-bs-target="#addmoneymodal" title="Add Money"><i className="bi bi-plus-lg"></i></a>
                                    </div>
                                </div>
                            </div>
                            <div className="card-footer">
                                <p className="text-secondary mb-1">AvailableBalance</p>
                                <h4 className="mb-1">$ 141525.54</h4>
                                <p className="small text-secondary">This week <b className="text-theme-1 theme-green">+ $ 152.51</b></p>
                            </div>
                        </div>
                    </div>
                    {/* finance chart*/}
                    <div className="col-12 col-xxl-6 order-3 order-xxl-2">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-header">
                                <div className="row gx-3 align-items-center">
                                    <div className="col">
                                        <h6 className="mb-0">Finance Insights</h6>
                                        <p className="text-secondary small mb-0">Track your income and expenses</p>
                                    </div>
                                    <div className="col-auto">

                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="mb-3">
                                    <AreaChart labels={['1', '2', '3', '4', '5', '7', '8', '9', '10']}
                                        datasets={[
                                            {
                                                data: [15, 10, 30, 15, 12, 10, 20, 15, 25, 20,],
                                                label: 'Expenses',
                                                color: '#e57463',
                                                fillColor: 'rgba(229, 116, 99, 0.5)',
                                                borderWidth: 2,
                                                pointBackgroundColor: '#e57463',
                                                pointBorderColor: '#ffffff',
                                                pointRadius: 4,
                                                tension: 0.4,
                                                borderColor: '#e57463',
                                            },
                                            {
                                                data: [20, 15, 15, 10, 20, 15, 25, 20, 10, 30],
                                                label: 'Income',
                                                color: '#00c851',
                                                fillColor: 'rgba(0, 200, 81, 0.5)',
                                                borderWidth: 2,
                                                pointBackgroundColor: '#00c851',
                                                pointBorderColor: '#ffffff',
                                                pointRadius: 4,
                                                tension: 0.4,
                                                borderColor: '#00c851',
                                            },
                                        ]}
                                        scalesx={true}
                                        scalesy={true}
                                        height={160} />
                                </div>

                                <div className="row gx-3">
                                    <div className="col">
                                        <p className="small text-secondary mb-1">Income</p>
                                        <div className="row gx-3 align-items-center">
                                            <div className="col-auto">
                                                <div className="avatar avatar-40 rounded bg-theme-1-subtle text-theme-1 theme-green">
                                                    <i className="bi bi-arrow-down-left h5"></i>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <h5 className="mb-0">$ 41.52 k</h5>
                                                <p className="small text-theme-1 theme-green"><i className="bi bi-caret-up-fill ms-1"></i> 2.15% </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col">
                                        <p className="small text-secondary mb-1">Expense</p>
                                        <div className="row gx-3 align-items-center">
                                            <div className="col-auto">
                                                <div className="avatar avatar-40 rounded bg-theme-1-subtle text-theme-1 theme-red">
                                                    <i className="bi bi-arrow-up-right h5"></i>
                                                </div>
                                            </div>
                                            <div className="col">
                                                <h5 className="mb-0">$ 31.32 k</h5>
                                                <p className="small text-theme-1 theme-red"><i className="bi bi-caret-up-fill ms-1"></i> 3.14% </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* crypto sent card */}
                    <div className="col-12 col-sm-6 col-xxl-3 order-2 order-xxl-3">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-header">
                                <h6 className="mb-1">Sent Crypto</h6>
                                <p className="text-secondary small">Track crypto transactions</p>
                            </div>
                            <div className="card-body">

                                <h5 className="mb-1">$ 12580.00</h5>
                                <p className="small text-secondary mb-0"><span className="text-theme-1 theme-green"><i className="bi bi-caret-up-fill"></i> 10.15%</span></p>
                                <p className="text-secondary small mb-lg-4">11 Transactions this month</p>

                                <Swiper
                                    slidesPerView="auto"
                                    spaceBetween={10}
                                    className="swipernav mb-3"
                                >
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <div className="avatar avatar-40 rounded-circle border border-dotted border-theme" data-bs-toggle="tooltip" title="Add Contact">
                                            <span className="h4 text-secondary">+</span>
                                        </div>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <figure className="avatar avatar-40 rounded-circle coverimg" data-bs-toggle="tooltip" title="Renny Johnson">
                                            <img src={asset('assets/img/adminuiux/user-7.jpg')} alt="finance-1" />
                                        </figure>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <figure className="avatar avatar-40 rounded-circle coverimg" data-bs-toggle="tooltip" title="John Doe">
                                            <img src={asset('assets/img/adminuiux/user-2.jpg')} alt="finance-1" />
                                        </figure>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <figure className="avatar avatar-40 rounded-circle coverimg" data-bs-toggle="tooltip" title="Mark William">
                                            <img src={asset('assets/img/adminuiux/user-9.jpg')} alt="finance-1" />
                                        </figure>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <figure className="avatar avatar-40 rounded-circle coverimg" data-bs-toggle="tooltip" title="Ketty Smith">
                                            <img src={asset('assets/img/adminuiux/user-5.jpg')} alt="finance-1" />
                                        </figure>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <figure className="avatar avatar-40 rounded-circle coverimg" data-bs-toggle="tooltip" title="Renny Johnson">
                                            <img src={asset('assets/img/adminuiux/user-7.jpg')} alt="finance-1" />
                                        </figure>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: 'auto' }}>
                                        <figure className="avatar avatar-40 rounded-circle coverimg" data-bs-toggle="tooltip" title="John Doe">
                                            <img src={asset('assets/img/adminuiux/user-2.jpg')} alt="finance-1" />
                                        </figure>
                                    </SwiperSlide>
                                </Swiper>
                                <div className="row gx-3 mb-3">
                                    <div className="col">
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <input type="number" className="form-control" placeholder="Amount..." />
                                            <select className="form-select maxwidth-dynamic" style={{ "--mw-dynamic": "80px" } as React.CSSProperties}>
                                                <option>BTC</option>
                                                <option>ETH</option>
                                                <option>LTC</option>
                                            </select>
                                            <button className="btn btn-square btn-theme"><i className="bi bi-send"></i></button>
                                        </div>
                                    </div>
                                </div>
                                <p className="text-secondary small"><b>$ 1256.15</b> in US Dollar</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row gx-3 gx-lg-4">
                    {/* targets and progress */}
                    <div className="col-12 col-md-6 col-lg-6 col-xl-4">
                        <div className="card position-relative mb-3 mb-lg-4 overflow-hidden">
                            <div className="coverimg position-absolute end-0 top-0 h-100 w-25">
                                <img src={asset('assets/img/background-image/backgorund-image-8.jpg')} alt="" />
                            </div>
                            <div className="row gx-3">
                                <div className="col-9">
                                    <div className="card-body">
                                        <div className="row gx-3">
                                            <div className="col-auto">
                                                <div className="rounded bg-theme-1 p-3">
                                                    <i className="bi bi-building d-inline-block mb-2"></i><br />
                                                    <p className="opacity-75 small mb-1">Deposit<br />Earning</p>
                                                    <h5>$2542</h5>
                                                </div>
                                            </div>
                                            <div className="col align-self-center">
                                                <p className="text-secondary small mb-0">United States</p>
                                                <p className="mb-4">New York</p>
                                                <p className="small text-secondary mb-2">$ 11600</p>
                                                <div className="progress height-dynamic bg-theme-1-subtle" style={{ "--h-dynamic": "5px" } as React.CSSProperties}>
                                                    <div className="progress-bar bg-theme-1" role="progressbar" style={{ "width": "45%" } as React.CSSProperties}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-3 position-relative">
                                </div>
                            </div>
                        </div>
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <div className="row gx-3">
                                    <div className="col-auto">
                                        <div className="rounded bg-theme-accent-1 text-white p-3">
                                            <p className="opacity-75 small mb-2">Assets</p>
                                            <h5>$2542</h5>
                                        </div>
                                    </div>
                                    <div className="col align-self-center">
                                        <div className="row gx-3 mb-4">
                                            <div className="col">
                                                <p className="text-secondary small mb-0">Targeted</p>
                                                <p>260</p>
                                            </div>
                                            <div className="col-auto text-end">
                                                <p className="text-secondary small mb-0">New</p>
                                                <p>120</p>
                                            </div>
                                        </div>
                                        <div className="progress height-dynamic bg-theme-accent-1-subtle" style={{ "--h-dynamic": "5px" } as React.CSSProperties}>
                                            <div className="progress-bar bg-theme-accent-1" role="progressbar" style={{ "width": "45%" } as React.CSSProperties} ></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* expense categories */}
                    <div className="col-6 col-md-6 col-lg-3 col-xl-2">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <p className="text-secondary small mb-1">Education</p>
                                <h5>$ 1075.15</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="w-100 height-60">
                                    <AreaChart labels={['1', '2', '3', '4', '5', '7', '8', '9', '10']}
                                        datasets={[
                                            {
                                                data: [15, 10, 30, 15, 12, 10, 20, 15, 25, 20,],
                                                label: 'New Users',
                                                color: '#0249e8',
                                                fillColor: 'rgba(72, 54, 240, 0.5)',
                                                borderWidth: 1.5,
                                                pointBackgroundColor: '#0249e8',
                                                pointBorderColor: '#ffffff',
                                                pointRadius: 0,
                                                tension: 0,
                                                borderColor: '#0249e8',
                                            },
                                        ]}
                                        scalesx={false}
                                        scalesy={false}
                                        height={60} />
                                </div>
                            </div>
                        </div>
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <p className="text-secondary small mb-1">Food</p>
                                <h5>$ 1542.65</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="w-100 height-60">
                                    <AreaChart labels={['1', '2', '3', '4', '5', '7', '8', '9', '10']}
                                        datasets={[
                                            {
                                                data: [14, 22, 18, 15, 25, 15, 10, 30, 15, 20,],
                                                label: 'New Users',
                                                color: '#08a046',
                                                fillColor: 'rgba(8, 160, 70, 0.5)',
                                                borderWidth: 1.5,
                                                pointBackgroundColor: '#08a046',
                                                pointBorderColor: '#ffffff',
                                                pointRadius: 0,
                                                tension: 0,
                                                borderColor: '#08a046',
                                            },
                                        ]}
                                        scalesx={false}
                                        scalesy={false}
                                        height={60} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-6 col-lg-3 col-xl-2">
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <p className="text-secondary small mb-1">Shipment</p>
                                <h5>$ 821.78</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="w-100 height-60">
                                    <AreaChart labels={['1', '2', '3', '4', '5', '7', '8', '9', '10']}
                                        datasets={[
                                            {
                                                data: [15, 10, 30, 15, 25, 20, 15, 12, 10, 20,],
                                                label: 'New Users',
                                                color: '#f19d00',
                                                fillColor: 'rgba(241, 157, 0, 0.5)',
                                                borderWidth: 1.5,
                                                pointBackgroundColor: '#f19d00',
                                                pointBorderColor: '#ffffff',
                                                pointRadius: 0,
                                                tension: 0,
                                                borderColor: '#f19d00',
                                            },
                                        ]}
                                        scalesx={false}
                                        scalesy={false}
                                        height={60} />
                                </div>
                            </div>
                        </div>
                        <div className="card mb-3 mb-lg-4">
                            <div className="card-body">
                                <p className="text-secondary small mb-1">Grocery</p>
                                <h5>$ 2530.36</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="w-100 height-60">
                                    <AreaChart labels={['1', '2', '3', '4', '5', '7', '8', '9', '10']}
                                        datasets={[
                                            {
                                                data: [15, 20, 15, 25, 20, 10, 30, 15, 12, 10,],
                                                label: 'New Users',
                                                color: '#c80036',
                                                fillColor: 'rgba(200, 0, 54, 0.5)',
                                                borderWidth: 1.5,
                                                pointBackgroundColor: '#c80036',
                                                pointBorderColor: '#ffffff',
                                                pointRadius: 0,
                                                tension: 0,
                                                borderColor: '#c80036',
                                            },
                                        ]}
                                        scalesx={false}
                                        scalesy={false}
                                        height={60} />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* finance card */}
                    <div className="col-12 col-md-12 col-xl-4 overflow-hidden">
                        <div className="card mb-3 mb-lg-4 overflow-hidden">
                            <div className="card-header">
                                <div className="row gx-3 align-items-center">
                                    <div className="col">
                                        <h6 className="mb-0">My Cards</h6>
                                        <p className="text-secondary small mb-0">You have 3 cards</p>
                                    </div>
                                    <div className="col-auto">
                                        <button className="btn btn-link btn-square" type="button">
                                            <i className="bi bi-plus-lg"></i>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body pt-0">
                                {/* Credit cards carousel card */}
                                <Swiper
                                    slidesPerView="auto"
                                    spaceBetween={18}
                                    className="swipernav overflow-visible"
                                >
                                    <SwiperSlide style={{ width: '240px' }}>
                                        <div className="card mb-2 theme-blue bg-theme-1 overflow-hidden">
                                            <div className="card-body position-relative z-index-1">
                                                <div className="row gx-3 align-items-center mb-4">
                                                    <div className="col-auto align-self-center">
                                                        <i className="bi bi-amazon fs-4"></i>
                                                    </div>
                                                    <div className="col text-end">
                                                        <p className="fs-12">
                                                            <span className="opacity-75 small">City Bank</span><br />
                                                            <span className="">Credit Card</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="fw-medium h6 mb-3">
                                                    000 0000 0001 546598
                                                </p>
                                                <div className="row gx-3 gx-lg-4">
                                                    <div className="col-auto fs-12">
                                                        <p className="mb-0 opacity-75 small">Expiry</p>
                                                        <p>09/023</p>
                                                    </div>
                                                    <div className="col text-end fs-12">
                                                        <p className="mb-0 opacity-75 small">Card Holder</p>
                                                        <p>DevUIUX</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gx-3 gx-lg-4">
                                            <div className="col">
                                                <p className="text-secondary small mb-1">Expense</p>
                                                <p>1500.00 <small className="text-theme-1 theme-green">18.0% <i className="bi bi-arrow-up"></i></small></p>
                                            </div>
                                            <div className="col-auto text-end">
                                                <p className="text-secondary small mb-1">Limit Remain</p>
                                                <p>13500.00</p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: '240px' }}>
                                        <div className="card theme-teal bg-theme-1 mb-2 overflow-hidden">
                                            <div className="card-body position-relative z-index-1">
                                                <div className="row gx-3 align-items-center mb-4">
                                                    <div className="col-auto align-self-center">
                                                        <i className="bi bi-apple fs-4"></i>
                                                    </div>
                                                    <div className="col text-end">
                                                        <p className="fs-12">
                                                            <span className="opacity-75 small">City Bank</span><br />
                                                            <span className="">Debit Card</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="fw-medium h6 mb-3">
                                                    000 0000 0001 546598
                                                </p>
                                                <div className="row gx-3 gx-lg-4">
                                                    <div className="col-auto fs-12">
                                                        <p className="mb-0 opacity-75 small">Expiry</p>
                                                        <p>09/023</p>
                                                    </div>
                                                    <div className="col text-end fs-12">
                                                        <p className="mb-0 opacity-75 small">Card Holder</p>
                                                        <p>DevUIUX</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gx-3 gx-lg-4">
                                            <div className="col">
                                                <p className="text-secondary small mb-1">Expense</p>
                                                <p>3650.00 <small className="text-danger">11.0% <i className="bi bi-arrow-down"></i></small></p>
                                            </div>
                                            <div className="col-auto text-end">
                                                <p className="text-secondary small mb-1">Limit Remain</p>
                                                <p>35500.00</p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                    <SwiperSlide style={{ width: '240px' }}>
                                        <div className="card theme-orange bg-theme-1 mb-2">
                                            <div className="card-body">
                                                <div className="row gx-3 align-items-center mb-4">
                                                    <div className="col-auto align-self-center">
                                                        <i className="bi bi-amazon fs-4"></i>
                                                    </div>
                                                    <div className="col text-end">
                                                        <p className="fs-12">
                                                            <span className="opacity-75 small">City Bank</span><br />
                                                            <span className="">Credit Card</span>
                                                        </p>
                                                    </div>
                                                </div>
                                                <p className="fw-medium h6 mb-3">
                                                    000 0000 0001 546598
                                                </p>
                                                <div className="row gx-3 gx-lg-4">
                                                    <div className="col-auto fs-12">
                                                        <p className="mb-0 opacity-75 small">Expiry</p>
                                                        <p>09/023</p>
                                                    </div>
                                                    <div className="col text-end fs-12">
                                                        <p className="mb-0 opacity-75 small">Card Holder</p>
                                                        <p>DevUIUX</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="row gx-3 gx-lg-4">
                                            <div className="col">
                                                <p className="text-secondary small mb-1">Expense</p>
                                                <p>1500.00 <small className="text-theme-1 theme-green">18.0 <i className="bi bi-arrow-up"></i></small></p>
                                            </div>
                                            <div className="col-auto text-end">
                                                <p className="text-secondary small mb-1">Limit Remain</p>
                                                <p>13500.00</p>
                                            </div>
                                        </div>
                                    </SwiperSlide>
                                </Swiper>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Grid table */}
                <div className="card mb-3 mb-lg-4">
                    <div className="card-header">
                        <div className="row gx-3">
                            <div className="col-12 col-md align-self-center mb-3 mb-lg-0">
                                <h6 className="mb-1">Expense</h6>
                                <p className="text-secondary small mb-0">Recently made expenses</p>
                            </div>
                            <div className="col-6 col-md-4 col-lg-4 col-xl-3 col-xxl-3">
                                <select className="form-select">
                                    <option>All Categories</option>
                                    <optgroup label="Food">
                                        <option>- Work</option>
                                        <option>- Home</option>
                                    </optgroup>
                                    <optgroup label="Children">
                                        <option>- Fees</option>
                                        <option>- Enjoyment</option>
                                    </optgroup>
                                    <optgroup label="Transportation">
                                        <option>- Personal</option>
                                        <option>- Work</option>
                                    </optgroup>
                                    <optgroup label="Home">
                                        <option>- Rent</option>
                                        <option>- Maintenance</option>
                                    </optgroup>
                                    <option>Personal</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <div className="col-6 col-md-auto ms-auto">
                                <div className="input-group ">
                                    <span className="input-group-text text-theme-1"><i className="bi bi-search"></i></span>
                                    <input type="text" className="form-control" placeholder="Search..." onChange={(e) => dtApi?.search(e.target.value).draw()} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="row gx-0 h-100">
                        <div className="col-12 col-lg-12 col-xl-8 col-xxl-8">
                            <div className="card-body pt-0">
                                <DataTable className="table mb-3" options={options}>
                                    <thead>
                                        <tr>
                                            <th className="width-10"></th>
                                            <th className="all">Expense</th>
                                            <th className="tablet desktop">Amount</th>
                                            <th className="desktop">Category</th>
                                            <th className="tablet desktop">Status</th>
                                            <th className="none">Deliver to</th>
                                            <th className="none">Address</th>
                                            <th className="none">Location</th>
                                            <th className="none">Payment</th>
                                            <th className="all"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <div className="avatar avatar-40 mb-0 coverimg rounded-circle bg-theme-1 theme-orange">
                                                            <i className="bi bi-currency-bitcoin"></i>
                                                        </div>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">BitCoins</p>
                                                        <p className="text-secondary small">1 way trip</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">0.000015</p>
                                                <p className="text-secondary small">BTC</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Traveling</p>
                                                <p className="text-secondary small">Work</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-green">Paid</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <figure className="avatar avatar-40 mb-0 bg-theme-1-subtle coverimg rounded-circle">
                                                            <img src={asset('assets/img/adminuiux/icon-3.png')} alt="" />
                                                        </figure>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">Starbucks</p>
                                                        <p className="text-secondary small">2 Coffee</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">6.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Food</p>
                                                <p className="text-secondary small">Work</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-theme-1 theme-blue">Waiting</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <div className="avatar avatar-40 mb-0 coverimg rounded-circle bg-theme-1 theme-green">
                                                            <i className="bi bi-cup-hot"></i>
                                                        </div>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">Starbucks</p>
                                                        <p className="text-secondary small">2 Coffee</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">6.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Food</p>
                                                <p className="text-secondary small">Work</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-theme-1 theme-blue">Waiting</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <figure className="avatar avatar-40 mb-0 bg-theme-1 coverimg rounded-circle">
                                                            <img src={asset('assets/img/adminuiux/icon-4.png')} alt="" />
                                                        </figure>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">PetMall</p>
                                                        <p className="text-secondary small">12 Items</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">180.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Grocery</p>
                                                <p className="text-secondary small">Home</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-red">Unpaid</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <div className="avatar avatar-40 mb-0 coverimg rounded-circle bg-theme-1 theme-cyan">
                                                            <i className="bi bi-basket"></i>
                                                        </div>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">EasyMall</p>
                                                        <p className="text-secondary small">12 Items</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">180.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Grocery</p>
                                                <p className="text-secondary small">Home</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-red">Unpaid</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <div className="avatar avatar-40 mb-0 coverimg rounded-circle bg-theme-1 theme-red">
                                                            <i className="bi bi-flower1"></i>
                                                        </div>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">Zomaatos</p>
                                                        <p className="text-secondary small">Meal</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">12.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Food</p>
                                                <p className="text-secondary small">Work</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-green">Paid</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <figure className="avatar avatar-40 mb-0 bg-theme-1 coverimg rounded-circle">
                                                            <img src={asset('assets/img/adminuiux/icon-2.png')} alt="" />
                                                        </figure>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">LeoCoin</p>
                                                        <p className="text-secondary small">1 way</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">0.000015</p>
                                                <p className="text-secondary small">BTC</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Traveling</p>
                                                <p className="text-secondary small">Work</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-primary">In-Progress</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-green">Paid</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <div className="avatar avatar-40 mb-0 coverimg rounded-circle bg-theme-1 theme-grey">
                                                            <i className="bi bi-car-front"></i>
                                                        </div>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">UserCars</p>
                                                        <p className="text-secondary small">Round Trip</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">251.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Travelling</p>
                                                <p className="text-secondary small">Home</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-theme-1 theme-blue">Waiting</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <figure className="avatar avatar-40 mb-0 bg-theme-1 coverimg rounded-circle">
                                                            <img src={asset('assets/img/adminuiux/icon-1.png')} alt="" />
                                                        </figure>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">Bus Service</p>
                                                        <p className="text-secondary small">Round Trip</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">251.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Travelling</p>
                                                <p className="text-secondary small">Home</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-theme-1 theme-orange">Pending</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-theme-1 theme-blue">Waiting</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <div className="row gx-3 gx-lg-4 align-items-center">
                                                    <div className="col-auto">
                                                        <figure className="avatar avatar-40 bg-theme-1 mb-0 coverimg rounded-circle">
                                                            <img src={asset('assets/img/adminuiux/icon-3.png')} alt="" />
                                                        </figure>
                                                    </div>
                                                    <div className="col">
                                                        <p className="mb-0">Zomaatos</p>
                                                        <p className="text-secondary small">Meal</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <p className="mb-0">12.00</p>
                                                <p className="text-secondary small">USD</p>
                                            </td>
                                            <td>
                                                <p className="mb-0">Food</p>
                                                <p className="text-secondary small">Work</p>
                                            </td>
                                            <td>
                                                <span className="badge badge-sm badge-light text-bg-success">Delivered</span>
                                            </td>
                                            <td>
                                                John Doe
                                            </td>
                                            <td>
                                                2356, Street-5, New York 4586, US
                                            </td>
                                            <td>
                                                <p className="mb-1">Lat: 5.678167, Long: 12.078613</p>
                                                <p className="text-secondary small">This can be a map also</p>
                                            </td>
                                            <td>
                                                <span className="badge rounded-5 bg-green">Paid</span>
                                            </td>
                                            <td>
                                                <div className="dropdown d-inline-block">
                                                    <a className="text-secondary no-caret" data-bs-toggle="dropdown" aria-expanded="false" data-bs-display="static" role="button">
                                                        <i className="bi bi-three-dots"></i>
                                                    </a>
                                                    <ul className="dropdown-menu dropdown-menu-end">
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Edit</a></li>
                                                        <li><a className="dropdown-item" href="javascript:void(0)">Move</a></li>
                                                        <li><a className="dropdown-item text-danger" href="javascript:void(0)">Delete</a></li>
                                                    </ul>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </DataTable>
                            </div>
                        </div>
                        <div className="col-12 col-lg-12 col-xl-4 col-xxl-4 overflow-hidden">
                            <div className="card-body py-0">
                                <div className="row h-100">
                                    <div className="col-12 text-center">
                                        <div className="width-240 my-3 my-lg-4 position-relative mx-auto">
                                            <DoughnutChart
                                                labels={['IA', 'NY', 'LA', 'SA', 'SV']}
                                                data={[40, 10, 15, 25, 10]}
                                                colors={['#e5e2ecff', '#3ee782', '#fb38b4ff', '#9c63f7ff', '#2ebeefff']}
                                                cutout="70%"
                                                height={220}
                                            />
                                            <div className="avatar avatar-190 rounded-circle position-absolute top-50 start-50 translate-middle z-index-1">
                                                <div className="w-100 lh-20">
                                                    <h4 className="mb-1">2,55.98K</h4>
                                                    <p className="text-theme-1 theme-green">18.71 (0.73%)</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 position-relative">
                                        <div className="row gx-3 gx-lg-4">
                                            <div className="col-6">
                                                <div className="row gx-3 mb-3">
                                                    <div className="col-auto">
                                                        <span className="avatar avatar-40 bg-theme-1 theme-teal rounded">
                                                            <i className="bi bi-truck h5"></i>
                                                        </span>
                                                    </div>
                                                    <div className="col">
                                                        <h5 className="mb-0">$ 120.15</h5>
                                                        <p className="text-secondary small">Transport</p>
                                                    </div>
                                                    <div className="col-12 mt-2">
                                                        <div className="row gx-2">
                                                            <div className="col-4">
                                                                <p className="text-secondary small">Home</p>
                                                            </div>
                                                            <div className="col-8">
                                                                <p className="text-theme-1 theme-green small">8.30 %</p>
                                                            </div>
                                                        </div>
                                                        <div className="row gx-2">
                                                            <div className="col-4">
                                                                <p className="text-secondary small">Work</p>
                                                            </div>
                                                            <div className="col-8">
                                                                <p className="text-theme-1 theme-green small">2.65 %</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="row gx-3 mb-3">
                                                    <div className="col-auto">
                                                        <span className="avatar avatar-40 bg-theme-1 theme-pink rounded">
                                                            <i className="bi bi-egg-fried h5"></i>
                                                        </span>
                                                    </div>
                                                    <div className="col">
                                                        <h5 className="mb-0">$ 168.00</h5>
                                                        <p className="text-secondary small">Food</p>
                                                    </div>
                                                    <div className="col-12 mt-2">
                                                        <div className="row gx-2">
                                                            <div className="col-4">
                                                                <p className="text-secondary small">Home</p>
                                                            </div>
                                                            <div className="col-8">
                                                                <p className="text-theme-1 theme-green small">5.30 %</p>
                                                            </div>
                                                        </div>
                                                        <div className="row gx-2">
                                                            <div className="col-4">
                                                                <p className="text-secondary small">Work</p>
                                                            </div>
                                                            <div className="col-8">
                                                                <p className="text-theme-1 theme-red small">2.00 %</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div className="row gx-3 mb-3">
                                                    <div className="col-auto">
                                                        <span className="avatar avatar-40  bg-theme-1 theme-purple rounded">
                                                            <i className="bi bi-person h5"></i>
                                                        </span>
                                                    </div>
                                                    <div className="col">
                                                        <h5 className="mb-0">$ 200.00</h5>
                                                        <p className="text-secondary small">Children</p>
                                                    </div>
                                                    <div className="col-12 mt-2">
                                                        <div className="row gx-2">
                                                            <div className="col-6">
                                                                <p className="text-secondary small">Fees</p>
                                                            </div>
                                                            <div className="col-6">
                                                                <p className="text-theme-1 theme-green small">530 USD</p>
                                                            </div>
                                                        </div>
                                                        <div className="row gx-2">
                                                            <div className="col-6">
                                                                <p className="text-secondary small text-truncated">Enjoyment</p>
                                                            </div>
                                                            <div className="col-6">
                                                                <p className="text-theme-1 theme-red small">10.00 %</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-6">
                                                <div className="row gx-3 mb-3">
                                                    <div className="col-auto">
                                                        <span className="avatar avatar-40 bg-theme-1 theme-cyan rounded">
                                                            <i className="bi bi-person h5"></i>
                                                        </span>
                                                    </div>
                                                    <div className="col">
                                                        <h5 className="mb-0">$ 160.00</h5>
                                                        <p className="text-secondary small">Other</p>
                                                    </div>
                                                    <div className="col-12 mt-2">
                                                        <div className="row gx-2">
                                                            <div className="col-6">
                                                                <p className="text-secondary text-truncated small">Maintenance</p>
                                                            </div>
                                                            <div className="col-6">
                                                                <p className="text-theme-1 theme-green small">110 USD</p>
                                                            </div>
                                                        </div>
                                                        <div className="row gx-2">
                                                            <div className="col-6">
                                                                <p className="text-secondary small">Grocery</p>
                                                            </div>
                                                            <div className="col-6">
                                                                <p className="text-theme-1 theme-red small">10.00 %</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* qr code modal */}
            <div className="modal adminuiux-modal fade" id="qrcodemodal" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-body pb-0">
                            <div className="text-center my-4">
                                <div className="avatar avatar-150 mb-4">
                                    <img src={asset('assets/img/adminuiux-qr.png')} alt="" className="mw-100 mx-auto" />
                                </div>
                                <h5 className="mb-0">Scan QR Code</h5>
                                <p className="text-secondary">To add money in wallet account</p>
                            </div>
                            <div className="row gx-3 gx-lg-4">
                                <div className="col mb-3">
                                    <p className="text-secondary">Name</p>
                                </div>
                                <div className="col-auto mb-3">
                                    <p className="fw-medium">DevUIUX</p>
                                </div>
                            </div>
                            <div className="row gx-3 gx-lg-4">
                                <div className="col mb-3">
                                    <p className="text-secondary">Virtual ID</p>
                                </div>
                                <div className="col-auto mb-3">
                                    <p className="fw-medium">00VFGBADMINUIUX02154869</p>
                                </div>
                            </div>
                            <div className="row gx-3 gx-lg-4">
                                <div className="col mb-3">
                                    <p className="text-secondary">Valid Till</p>
                                </div>
                                <div className="col-auto mb-3">
                                    <p className="fw-medium">26-09-2026 12:00 am</p>
                                </div>
                            </div>
                            <div className="row gx-3 gx-lg-4">
                                <div className="col mb-3">
                                    <p className="text-secondary">Generated on</p>
                                </div>
                                <div className="col-auto mb-3">
                                    <p className="fw-medium">12-04-2024 6:25 am <i className="bi bi-arrow-clockwise text-secondary" data-bs-toggle="tooltip" title="Re-generate"></i></p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer justify-content-center">
                            <button type="button" className="btn btn-link theme-red" data-bs-dismiss="modal">Close</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* send money modal */}
            <div className="modal adminuiux-modal fade" id="sendmoneymodal" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <p className="modal-title h5">Send Money</p>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">

                            <p className="">Recently connected contacts</p>
                            {/* recent connection */}
                            <Swiper
                                slidesPerView="auto"
                                spaceBetween={10}
                                className="swipernav mb-3"
                            >
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle border border-dotted border-theme">
                                        <span className="h4">+</span>
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-1.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-3.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-4.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-5.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-6.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-8.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-3.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-4.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                            </Swiper>
                            <div className="form-floating mb-4">
                                <input type="text" className="form-control" id="contact1" placeholder="Enter contact..." defaultValue="Jonathan" />
                                <label htmlFor="contact">Send money to</label>
                            </div>

                            <input type="number" className="form-control form-control-lg text-center mb-3" id="ihavei" placeholder="Convert Amount..." defaultValue="100.00" />
                            <div className="row gx-3 mb-3">
                                <div className="col">
                                    <div className="form-floating mb-1">
                                        <select className="form-select" id="ihavecurrencyi" defaultValue={"USD"}>
                                            <option value={"USD"}>USD Wallet</option>
                                            <option value={"CAD"}>CAD Wallet</option>
                                            <option value={"AUD"}>AUD Wallet</option>
                                        </select>
                                        <label htmlFor="ihavecurrencyi">From...</label>
                                    </div>
                                    <p className="small text-secondary text-center">1.00 USD</p>
                                </div>
                                <div className="col-auto">
                                    <button className="btn btn-square btn-theme mt-2"><i className="bi bi-arrow-left-right"></i></button>
                                </div>
                                <div className="col">
                                    <div className="form-floating mb-1">
                                        <select className="form-select" id="ihavecurrency2i" defaultValue={"CAD"}>
                                            <option value={"USD"}>USD</option>
                                            <option value={"CAD"}>CAD</option>
                                            <option value={"AUD"}>AUD</option>
                                        </select>
                                        <label htmlFor="ihavecurrency2i">Send in...</label>
                                    </div>
                                    <p className="small text-secondary text-center">1.38 CAD</p>
                                </div>
                            </div>
                            <div className="text-center">
                                <h5 className="fw-normal"><b className="fw-bold">Great!</b> You are going to send</h5>
                                <h1 className="mb-0 text-theme-1">100.00</h1>
                                <p className="text-secondary small">in Canadian Dollar</p>
                            </div>

                            <div className="alert alert-danger alert-dismissible fade show mt-4" role="alert">
                                <strong><i className="bi bi-exclamation-triangle me-1"></i> Low balance!</strong><br />
                                <small>Your wallet account does not have enough money to send. Kindly add money or choose different wallet account.</small>
                                <button type="button" className="btn-close" data-bs-dismiss="alert"></button>
                            </div>
                        </div>
                        <div className="modal-footer justify-content-between">
                            <button className="btn btn-theme">Send Money</button>
                            <button type="button" className="btn btn-link mx-2 theme-red" data-bs-dismiss="modal">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* receive money modal */}
            <div className="modal adminuiux-modal fade" id="receivemoneymodal" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <p className="modal-title h5">Receive/Request Money</p>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">

                            <p className="">Recently connected contacts</p>
                            {/* recent connection */}
                            <Swiper
                                slidesPerView="auto"
                                spaceBetween={10}
                                className="swipernav mb-4"
                            >
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle border border-dotted border-theme">
                                        <span className="h4">+</span>
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-1.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-3.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-4.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-5.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-6.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-8.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-3.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                                <SwiperSlide style={{ width: 'auto' }}>
                                    <div className="avatar avatar-40 rounded-circle coverimg">
                                        <img src={asset('assets/img/adminuiux/user-4.jpg')} alt="" />
                                    </div>
                                </SwiperSlide>
                            </Swiper>
                            <div className="form-floating mb-4">
                                <input type="text" className="form-control" id="contact" placeholder="Enter contact..." defaultValue="Jonathan" />
                                <label htmlFor="contact">Receive money from</label>
                            </div>

                            <div className="row gx-3 mb-3">
                                <div className="col">
                                    <input type="number" className="form-control form-control-lg" placeholder="Convert Amount..." defaultValue="100.00" />
                                </div>
                                <div className="col-auto">
                                    <select className="form-select form-select-lg" defaultValue={"usd"}>
                                        <option value={"usd"}>USD Wallet</option>
                                        <option value={"cad"}>CAD Wallet</option>
                                        <option value={"aud"}>AUD Wallet</option>
                                    </select>
                                </div>
                            </div>
                            <div className="text-center">
                                <h5 className="fw-normal"><b className="fw-bold">Great!</b> You are going to receive</h5>
                                <h1 className="mb-0 text-theme-1">100.00</h1>
                                <p className="text-secondary small">in CAD wallet</p>
                            </div>
                        </div>
                        <div className="modal-footer justify-content-between">
                            <button className="btn btn-theme">Request Money</button>
                            <button type="button" className="btn btn-link mx-2 theme-red" data-bs-dismiss="modal">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>

            {/* add money modal */}
            <div className="modal adminuiux-modal fade" id="addmoneymodal" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <p className="modal-title h5">Add Money</p>
                            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
                        </div>
                        <div className="modal-body">
                            <div className="row gx-3 mb-3">
                                <div className="col">
                                    <input type="number" className="form-control form-control-lg text-center" placeholder="Add Amount..." defaultValue={"100.00"} />
                                </div>
                                <div className="col-auto">
                                    <select className="form-select form-select-lg">
                                        <option>USD Wallet</option>
                                        <option>CAD Wallet</option>
                                        <option>AUD Wallet</option>
                                    </select>
                                </div>
                            </div>
                            <div className="text-center mb-4">
                                <h5 className="fw-normal"><b className="fw-bold">Great!</b> You are going to add</h5>
                                <h1 className="mb-0 text-theme-1">100.00</h1>
                                <p className="text-secondary small">money in CAD Wallet</p>
                            </div>
                            <h6>Add money using</h6>
                            <ul className="list-group adminuiux-list-group">
                                <li className="list-group-item">
                                    <input className="form-check-input me-1" type="radio" name="listGroupRadio" value="" id="firstRadio2" defaultChecked />
                                    <label className="form-check-label stretched-link" htmlFor="firstRadio2">Net Banking</label>
                                </li>
                                <li className="list-group-item">
                                    <input className="form-check-input me-1" type="radio" name="listGroupRadio" value="" id="secondRadio2" />
                                    <label className="form-check-label stretched-link" htmlFor="secondRadio2">Credit / Debit Card</label>
                                </li>
                                <li className="list-group-item">
                                    <input className="form-check-input me-1" type="radio" name="listGroupRadio" value="" id="thirdRadio2" />
                                    <label className="form-check-label stretched-link" htmlFor="thirdRadio2">UPI Payment</label>
                                </li>
                            </ul>
                        </div>
                        <div className="modal-footer justify-content-between">
                            <button className="btn btn-theme">Pay Now</button>
                            <button type="button" className="btn btn-link mx-2 theme-red" data-bs-dismiss="modal">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>

            <MobileFooter />
        </>
    );
};

export default FinanceDashboard;
